import { useState, useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Info, Send, Loader2, Calendar, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { de } from "date-fns/locale";
import { format, isBefore, startOfDay } from "date-fns";
import { DEVICES, DEVICE_KAUTION, PERIOD_LABELS } from "@/data/devices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function DevicePage() {
  const { slug } = useParams();
  const device = DEVICES[slug];

  const [selectedPeriod, setSelectedPeriod] = useState("24h");
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    const fetchBlockedDates = async () => {
      try {
        const res = await axios.get(`${API}/blocked-dates`);
        setBlockedDates(
          res.data
            .map((d) => new Date(d.date))
            .filter((d) => !isNaN(d.getTime()))
        );
      } catch (err) {
        console.error("Error fetching blocked dates:", err);
      }
    };
    fetchBlockedDates();
  }, []);

  if (!device) {
    return <Navigate to="/geraete" replace />;
  }

  const isDateBlocked = (date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;
    return blockedDates.some(
      (blocked) => format(blocked, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
    );
  };

  const calculatePrice = () => device.pricing[selectedPeriod];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate) {
      toast.error("Bitte wählen Sie ein Startdatum");
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        ...formData,
        product_id: device.slug,
        rental_period: selectedPeriod,
        start_date: format(selectedDate, "yyyy-MM-dd"),
      };
      await axios.post(`${API}/bookings`, bookingData);

      toast.success("Buchungsanfrage gesendet!", {
        description: "Wir melden uns schnellstmöglich bei Ihnen.",
      });

      setFormData({ first_name: "", last_name: "", email: "", phone: "", message: "" });
      setSelectedDate(null);
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid={`device-page-${device.slug}`}>
      {/* Hero */}
      <section className="bg-zinc-50 py-12 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm text-orange-600 font-medium mb-2">{device.category}</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight">
              {device.title}
            </h1>
            <p className="text-lg text-zinc-600 mt-3 max-w-3xl">{device.subline}</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Image */}
            <div>
              <div className="rounded-2xl overflow-hidden bg-white border border-zinc-200">
                <img
                  src={device.image}
                  alt={device.shortTitle}
                  className="w-full aspect-[4/3] object-contain p-4"
                  data-testid="device-image"
                />
              </div>
              <p className="text-xs text-zinc-400 mt-2 text-center">Produktfoto – Abbildung ähnlich</p>
            </div>

            {/* Right - Details & Booking */}
            <div>
              {/* Description */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4">Beschreibung</h2>
                <p className="text-zinc-600 leading-relaxed">{device.description}</p>
                <ul className="mt-4 space-y-2">
                  {device.highlights.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-zinc-700">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Period Selection */}
              <div className="mb-8 p-6 bg-zinc-50 rounded-xl border border-zinc-100">
                <h3 className="font-semibold text-zinc-900 mb-4">Mietzeitraum wählen</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedPeriod(key)}
                      data-testid={`period-${key}`}
                      className={`p-3 rounded-lg border text-center transition-colors cursor-pointer ${
                        selectedPeriod === key
                          ? "border-orange-500 bg-orange-50"
                          : "border-zinc-200 bg-white hover:bg-zinc-50"
                      }`}
                    >
                      <p className="text-xs text-zinc-500">{label}</p>
                      <p className="text-lg font-bold text-zinc-900">€{device.pricing[key]}</p>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-zinc-500 mt-4 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Kaution: €{DEVICE_KAUTION} (bei Abholung)
                </p>
              </div>

              {/* Price Summary */}
              <div className="mb-8 p-6 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-zinc-600">Mietpreis</p>
                    <p className="text-3xl font-bold text-orange-600" data-testid="device-price">
                      €{calculatePrice()}
                    </p>
                    <p className="text-sm text-zinc-500">+ €{DEVICE_KAUTION} Kaution</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-zinc-600">{PERIOD_LABELS[selectedPeriod]}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Optional Accessories */}
          {device.accessories && (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-12"
              data-testid="device-accessories"
            >
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-zinc-200">
                <h2 className="text-xl font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-orange-500" />
                  {device.accessories.title}
                </h2>
                <p className="text-zinc-600 leading-relaxed mb-6">{device.accessories.text}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {device.accessories.options.map((opt, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between gap-4 p-4 rounded-xl bg-zinc-50 border border-zinc-100"
                    >
                      <span className="text-sm text-zinc-700">{opt.name}</span>
                      <span className="text-sm font-bold text-orange-600 whitespace-nowrap">
                        {opt.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Booking Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-16"
          >
            <div className="bg-zinc-50 rounded-2xl p-8 border border-zinc-100">
              <h2 className="text-2xl font-bold text-zinc-950 mb-2">Buchungsanfrage senden</h2>
              <p className="text-zinc-600 mb-8">
                Wählen Sie Ihr Wunschdatum und senden Sie Ihre Anfrage. Wir bestätigen die Buchung
                und vereinbaren die genaue Abholzeit.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-orange-500" />
                      Startdatum wählen
                    </h3>
                    <div className="bg-white rounded-xl p-4 border border-zinc-200 inline-block">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateBlocked}
                        locale={de}
                        className="rounded-md"
                      />
                    </div>
                    {selectedDate && (
                      <p className="mt-4 text-sm text-zinc-600">
                        Gewähltes Datum:{" "}
                        <strong>{format(selectedDate, "dd. MMMM yyyy", { locale: de })}</strong>
                      </p>
                    )}
                  </div>

                  {/* Contact Form */}
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">Vorname *</Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          required
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="Max"
                          className="border-zinc-200"
                          data-testid="device-input-first-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Nachname *</Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          required
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Mustermann"
                          className="border-zinc-200"
                          data-testid="device-input-last-name"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="max@beispiel.at"
                        className="border-zinc-200"
                        data-testid="device-input-email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+43 ..."
                        className="border-zinc-200"
                        data-testid="device-input-phone"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Nachricht (optional)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Anmerkungen, Wunschzeit für Abholung, etc."
                        className="border-zinc-200 resize-none"
                        rows={3}
                        data-testid="device-input-message"
                      />
                    </div>

                    <div className="bg-zinc-100 rounded-lg p-4 text-sm text-zinc-600">
                      <p className="font-medium text-zinc-900 mb-1">Hinweis zur Abholung</p>
                      <p>
                        Die genaue Abholzeit wird nach Bestätigung der Buchung vereinbart. Zahlung
                        erfolgt bei Abholung.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !selectedDate}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg"
                      data-testid="device-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Buchungsanfrage senden
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Specifications */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-zinc-950 mb-6">Technische Daten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {device.specCards.map((card, cIdx) => (
                <div key={cIdx} className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                  <div
                    className={`px-6 py-4 border-b ${
                      card.highlight ? "bg-orange-50 border-orange-100" : "bg-zinc-50 border-zinc-200"
                    }`}
                  >
                    <h3 className="font-semibold text-zinc-900">{card.title}</h3>
                  </div>
                  <div>
                    {card.specs.map((spec, idx) => (
                      <div
                        key={idx}
                        className={`flex justify-between gap-4 px-6 py-3 text-sm ${
                          idx % 2 === 0 ? "bg-zinc-50" : "bg-white"
                        }`}
                      >
                        <span className="text-zinc-600 flex-shrink-0">{spec.label}</span>
                        <span className="font-medium text-zinc-900 text-right">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Back link */}
          <div className="mt-10 text-center">
            <Link
              to="/geraete"
              className="text-sm text-orange-600 hover:underline font-medium"
              data-testid="device-back-link"
            >
              ← Alle Geräte ansehen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
